import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';

import { actionKindMeta } from './actionConfig';
import { actionStatusDisplay, Tone } from './StatusBadge';

type Indicator = 'complete' | 'active' | 'pending' | 'muted';

interface RailAction {
  key: string;
  label: string;
  indicator: Indicator;
}

interface RailStep {
  key: string;
  label: string;
  indicator: Indicator;
  // Shown nested only when a category has more than one action (design §12 roll-up).
  children: RailAction[];
}

function indicatorForTone(tone: Tone): Indicator {
  if (tone === 'success') return 'complete';
  if (tone === 'warning' || tone === 'info' || tone === 'danger') return 'active';
  return 'pending';
}

function actionIndicator(action: WorkflowAction): Indicator {
  if (action.status === ActionStatus.CANCELED || action.status === ActionStatus.SKIPPED) return 'muted';
  if (action.stale) return 'active'; // stale needs a person (P1-1)
  return indicatorForTone(actionStatusDisplay(action.status).tone);
}

// A category's indicator is a roll-up of its actions (design §12): needs-a-person
// wins, then done-when-all-done, else still going.
function categoryIndicator(actions: WorkflowAction[]): Indicator {
  if (actions.some((a) => a.isActionRequired || a.stale || a.status === ActionStatus.FLAGGED)) return 'active';
  if (actions.length > 0 && actions.every((a) => a.isCompleted)) return 'complete';
  if (actions.some((a) => a.status === ActionStatus.RUNNING)) return 'active';
  if (actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)) return 'muted';
  return 'pending';
}

// Patient details is a synthetic first step — the value-entry that gates the run
// in Phase 0 (the pills). Then one row per category, with action children only
// where a category has more than one action.
function buildSteps(workflow: Workflow): RailStep[] {
  const hasDemographics = ['first_name', 'last_name', 'date_of_birth'].every(
    (name) => !!workflow.valueFor(name)?.value
  );
  const steps: RailStep[] = [
    {
      key: 'demographics',
      label: 'Patient details',
      indicator: hasDemographics ? 'complete' : workflow.isTerminal ? 'muted' : 'active',
      children: [],
    },
  ];

  workflow.actionsByCategory().forEach(({ category, actions }) => {
    steps.push({
      key: category.key,
      label: category.name,
      indicator: categoryIndicator(actions),
      children:
        actions.length > 1
          ? actions.map((action) => ({
              key: action.id,
              label: actionKindMeta(action.kind)?.label ?? action.defaultLabel,
              indicator: actionIndicator(action),
            }))
          : [],
    });
  });

  return steps;
}

export default function ProgressRail({ workflow }: { workflow: Workflow }) {
  const steps = buildSteps(workflow);

  return (
    <Rail data-testid='progress-rail'>
      <BodySmallBold>Progress</BodySmallBold>
      {workflow.createdAt && <LastUpdate>Referral created on {simpleDateAtTime(workflow.createdAt)}</LastUpdate>}
      <Steps>
        {steps.map((step) => (
          <div key={step.key}>
            <StepRow>
              <Dot indicator={step.indicator} />
              <StepLabel $muted={step.indicator === 'muted' || step.indicator === 'pending'}>{step.label}</StepLabel>
            </StepRow>
            {step.children.map((child) => (
              <ChildRow key={child.key}>
                <Dot indicator={child.indicator} small />
                <ChildLabel $muted={child.indicator === 'muted' || child.indicator === 'pending'}>
                  {child.label}
                </ChildLabel>
              </ChildRow>
            ))}
          </div>
        ))}
      </Steps>
    </Rail>
  );
}

function Dot({ indicator, small }: { indicator: Indicator; small?: boolean }) {
  const size = small ? 12 : 16;
  return (
    <Indicator $indicator={indicator} $size={size}>
      {indicator === 'complete' && <CircleCheckIcon width={size} height={size} />}
    </Indicator>
  );
}

const Rail = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  padding: 20px;
`;

const LastUpdate = styled(BodySmall)`
  display: block;
  margin-top: 6px;
  color: ${colors.black50};
`;

const Steps = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-left: 28px;
`;

const Indicator = styled.span<{ $indicator: Indicator; $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;

  ${({ $indicator }) => {
    if ($indicator === 'complete') return `color: ${colors.actionGreen};`;
    if ($indicator === 'active') return `border-radius: 50%; background: ${colors.secondaryOrange};`;
    return `border-radius: 50%; border: 1.5px solid ${colors.black25}; box-sizing: border-box;`;
  }}
`;

const StepLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black)};
  font-weight: 700;
`;

const ChildLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black75)};
`;
